import { useEffect, useState } from "react";
import {
  useRouterState,
  useNavigate,
  type NavigateOptions,
} from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { signOut } from "@/lib/auth";
import { CommandPalette } from "./CommandPalette";
import { InboxBell } from "./InboxBell";
import { Icon } from "./Icon";
import { Avatar } from "./Avatar";
import { Button } from "./Button";

interface Crumb {
  label: string;
  to?: string;
}

interface TopBarProps {
  onMenu?: () => void;
  /** When true the burger/menu button is always rendered (sidebar is hidden
   * or collapsed). Defaults to false: the button only renders if `onMenu`
   * is provided AND the legacy media-query-based CSS shows it (<=820px). */
  showMenu?: boolean;
  title?: string;
  crumbs?: Crumb[];
}

export function TopBar({ onMenu, showMenu, title, crumbs }: TopBarProps) {
  const router = useRouterState();
  const navigate = useNavigate();
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [avatarOpen, setAvatarOpen] = useState(false);

  const me = useQuery({
    queryKey: queryKeys.me(),
    queryFn: api.me,
  });

  // Used by the Create menu to default-target the most relevant project for
  // "New task" when no project is in scope. Falls back to /projects (the
  // picker) when the user has zero projects.
  const projects = useQuery({
    queryKey: queryKeys.projects(),
    queryFn: api.listProjects,
  });
  const defaultProjectId = projects.data?.projects[0]?.id ?? null;

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      } else if (e.key === "Escape") {
        setPaletteOpen(false);
        setCreateOpen(false);
        setAvatarOpen(false);
      }
    }
    // Allow any descendant (e.g. the Sidebar search shim) to request the
    // palette via a custom event — TopBar owns the palette state.
    function onOpenPalette() {
      setPaletteOpen(true);
    }
    window.addEventListener("keydown", onKey);
    window.addEventListener("command-palette:open", onOpenPalette);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("command-palette:open", onOpenPalette);
    };
  }, []);

  // Auto-derive page title from path if no explicit title/crumbs given.
  const derivedTitle =
    title ||
    (() => {
      const path = router.location.pathname;
      if (path === "/") return "Home";
      if (path === "/my-tasks") return "My Tasks";
      if (path === "/inbox") return "Notifications";
      if (path === "/files") return "Files";
      if (path === "/team") return "Team";
      if (path.startsWith("/projects")) return "Projects";
      if (path.startsWith("/settings")) return "Settings";
      return "";
    })();

  return (
    <>
      <header className="topbar">
        {onMenu && (
          <button
            type="button"
            className={`tb-iconbtn tb-burger${showMenu ? " tb-burger-show" : ""}`}
            onClick={onMenu}
            aria-label="Toggle navigation"
          >
            <Icon name="burger" />
          </button>
        )}
        <div className="tb-title">
          {crumbs ? (
            <div className="crumbs">
              {crumbs.map((c, i) => (
                <span key={i}>
                  {c.to ? (
                    <a
                      onClick={(e) => {
                        e.preventDefault();
                        navigate({ to: c.to! });
                      }}
                      href={c.to}
                    >
                      {c.label}
                    </a>
                  ) : (
                    c.label
                  )}
                  {i < crumbs.length - 1 && " / "}
                </span>
              ))}
            </div>
          ) : (
            <span className="pg">{derivedTitle}</span>
          )}
        </div>
        <div className="tb-actions">
          <button
            type="button"
            className="tb-iconbtn"
            onClick={() => setPaletteOpen(true)}
            aria-label="Search (⌘K)"
            title="Search (⌘K)"
          >
            <Icon name="search" />
          </button>
          <InboxBell />
          <div style={{ position: "relative" }}>
            <Button
              variant="primary"
              size="sm"
              data-create-trigger
              onClick={() => setCreateOpen((o) => !o)}
            >
              <Icon name="plus" />
              Create
            </Button>
            {createOpen && (
              <CreateMenu
                defaultProjectId={defaultProjectId}
                onSelect={(target) => {
                  setCreateOpen(false);
                  // `navigate` is typed against the route tree — cast through
                  // NavigateOptions so the runtime path-with-params handoff
                  // doesn't require us to surface a discriminated union here.
                  navigate(target as NavigateOptions);
                }}
                onClose={() => setCreateOpen(false)}
              />
            )}
          </div>
          <div style={{ position: "relative" }}>
            <button
              type="button"
              className="tb-iconbtn tb-avatar-btn"
              data-avatar-trigger
              aria-label="Account menu"
              aria-haspopup="menu"
              aria-expanded={avatarOpen}
              onClick={() => setAvatarOpen((o) => !o)}
              style={{
                padding: 0,
                border: 0,
                background: "transparent",
                cursor: "pointer",
                borderRadius: "999px",
              }}
            >
              {me.data ? (
                <Avatar
                  id={me.data.user.id}
                  name={me.data.user.name || me.data.user.email}
                  size="md"
                  image={me.data.user.image}
                />
              ) : (
                <Icon name="user" />
              )}
            </button>
            {avatarOpen && (
              <AvatarMenu
                onClose={() => setAvatarOpen(false)}
                onNavigate={(target) => {
                  setAvatarOpen(false);
                  navigate({ to: target });
                }}
              />
            )}
          </div>
        </div>
      </header>
      <CommandPalette
        open={paletteOpen}
        onClose={() => setPaletteOpen(false)}
      />
    </>
  );
}

type CreateNav =
  | { to: "/projects/$projectId/tasks/new"; params: { projectId: string } }
  | { to: "/projects/new" }
  | { to: "/projects" }
  | { to: "/admin/invites" }
  | { to: "/files" };

function CreateMenu({
  defaultProjectId,
  onSelect,
  onClose,
}: {
  defaultProjectId: string | null;
  onSelect: (target: CreateNav) => void;
  onClose: () => void;
}) {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const t = e.target as HTMLElement;
      if (!t.closest(".create-menu") && !t.closest("[data-create-trigger]"))
        onClose();
    }
    // Attach on the next tick so the trigger's own click (which opened the
    // menu) doesn't immediately bubble up to this listener and close it.
    const id = setTimeout(() => {
      window.addEventListener("click", onClick);
    }, 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("click", onClick);
    };
  }, [onClose]);

  // "New task" needs a project in scope. When the user has at least one
  // project we default to the first (alphabetical/last-updated, depending on
  // server order). When they have zero projects we fall back to the Projects
  // list so they can create one first.
  const newTaskTarget: CreateNav = defaultProjectId
    ? {
        to: "/projects/$projectId/tasks/new",
        params: { projectId: defaultProjectId },
      }
    : { to: "/projects" };

  return (
    <div
      className="create-menu show"
      style={{ position: "absolute", top: "calc(100% + 6px)", right: 0 }}
    >
      <MenuItem
        icon="check"
        label="New task"
        description={
          defaultProjectId
            ? "Add a task to a project"
            : "Create a project first"
        }
        onClick={() => onSelect(newTaskTarget)}
      />
      <MenuItem
        icon="folder"
        label="New project"
        description="Create a new project"
        onClick={() => onSelect({ to: "/projects/new" })}
      />
      <MenuItem
        icon="user"
        label="Invite teammate"
        description="Send an invite link"
        onClick={() => onSelect({ to: "/admin/invites" })}
      />
      <MenuItem
        icon="clip"
        label="Upload file"
        description="Go to Files"
        onClick={() => onSelect({ to: "/files" })}
      />
    </div>
  );
}

function MenuItem({
  icon,
  label,
  description,
  onClick,
}: {
  icon: import("./Icon").IconName;
  label: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "8px 9px",
        background: "transparent",
        border: 0,
        cursor: "pointer",
        width: "100%",
        textAlign: "left",
        borderRadius: "var(--r-sm)",
        font: "inherit",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.background = "var(--subtle)")}
      onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
    >
      <span className="cm-ic">
        <Icon name={icon} />
      </span>
      <span>
        <div className="cm-t">{label}</div>
        <div className="cm-d">{description}</div>
      </span>
    </button>
  );
}

function AvatarMenu({
  onClose,
  onNavigate,
}: {
  onClose: () => void;
  onNavigate: (path: string) => void;
}) {
  useEffect(() => {
    function onClick(e: MouseEvent) {
      const t = e.target as HTMLElement;
      if (!t.closest(".avatar-menu") && !t.closest("[data-avatar-trigger]"))
        onClose();
    }
    // Defer attach so the trigger's open-click doesn't immediately close it.
    const id = setTimeout(() => {
      window.addEventListener("click", onClick);
    }, 0);
    return () => {
      clearTimeout(id);
      window.removeEventListener("click", onClick);
    };
  }, [onClose]);

  async function handleSignOut() {
    onClose();
    try {
      await signOut();
    } finally {
      // Force a hard reload so the auth-gated router sees a fresh session.
      // /login is the real route — "/sign-in" never existed and landed on
      // the router's not-found state after sign-out (review finding).
      window.location.assign("/login");
    }
  }

  return (
    <div
      role="menu"
      aria-label="Account menu"
      className="avatar-menu create-menu show"
      style={{
        position: "absolute",
        top: "calc(100% + 6px)",
        right: 0,
        minWidth: 180,
      }}
    >
      <MenuItem
        icon="user"
        label="Account"
        description="Your account"
        onClick={() => onNavigate("/settings/account")}
      />
      <MenuItem
        icon="settings"
        label="Settings"
        description="Preferences"
        onClick={() => onNavigate("/settings/appearance")}
      />
      <button
        type="button"
        role="menuitem"
        onClick={handleSignOut}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "8px 9px",
          background: "transparent",
          border: 0,
          cursor: "pointer",
          width: "100%",
          textAlign: "left",
          borderRadius: "var(--r-sm)",
          font: "inherit",
        }}
        onMouseEnter={(e) =>
          (e.currentTarget.style.background = "var(--subtle)")
        }
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <span className="cm-ic">
          <Icon name="close" />
        </span>
        <span>
          <div className="cm-t">Sign out</div>
          <div className="cm-d">End this session</div>
        </span>
      </button>
    </div>
  );
}
